/** Live SSE throws plus catch-up rows so offline targets still see them on Leagues open. */

export const LEAGUE_REACTION_EVENT = "c8-league-reaction";

export type LeagueReactionEmoji = "poop" | "rofl" | "rocket" | "fire" | "clap" | "cry";

export const LEAGUE_REACTION_GLYPH: Record<LeagueReactionEmoji, string> = {
    poop: "💩",
    rofl: "🤣",
    rocket: "🚀",
    fire: "🔥",
    clap: "👏",
    cry: "😭",
};

export const LEAGUE_REACTION_EMOJI_OPTIONS: LeagueReactionEmoji[] = [
    "poop",
    "rofl",
    "rocket",
    "fire",
    "clap",
    "cry",
];

/** Newest throws kept on the league payload (matches backend REACTION_ACTIVITY_CAP). */
export const LEAGUE_REACTION_ACTIVITY_CAP = 60;
/**
 * Soft cap when merging into the main Activity list so throw spam cannot bury play rows.
 * Storage still keeps {@link LEAGUE_REACTION_ACTIVITY_CAP}.
 */
export const LEAGUE_REACTION_FEED_CAP = 20;

export type LeagueReactionPayload = {
    reactionId: string;
    leagueId: string;
    fromUserId: number;
    fromPlayerName: string;
    targetUserId: number;
    emoji: LeagueReactionEmoji;
    /** Catch-up only: extra missed throws collapsed into this replay. */
    extraCount?: number;
};

export function isLeagueReactionEmoji(value: unknown): value is LeagueReactionEmoji {
    return (
        value === "poop" ||
        value === "rofl" ||
        value === "rocket" ||
        value === "fire" ||
        value === "clap" ||
        value === "cry"
    );
}

/** Normalize an SSE / API payload into a typed reaction, or null if invalid. */
export function parseLeagueReactionPayload(raw: unknown): LeagueReactionPayload | null {
    if (!raw || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    const reactionId = String(o.reactionId ?? "").trim();
    const leagueId = String(o.leagueId ?? "").trim();
    const fromUserId = parseInt(String(o.fromUserId ?? ""), 10);
    const targetUserId = parseInt(String(o.targetUserId ?? ""), 10);
    const fromPlayerName = String(o.fromPlayerName ?? "").trim();
    const emoji = o.emoji;
    if (!reactionId || !leagueId) return null;
    if (!Number.isFinite(fromUserId) || fromUserId <= 0) return null;
    if (!Number.isFinite(targetUserId) || targetUserId <= 0) return null;
    if (!isLeagueReactionEmoji(emoji)) return null;
    const extraRaw = parseInt(String(o.extraCount ?? ""), 10);
    const extraCount = Number.isFinite(extraRaw) && extraRaw > 0 ? extraRaw : 0;
    return {
        reactionId,
        leagueId,
        fromUserId,
        fromPlayerName: fromPlayerName || `Player ${fromUserId}`,
        targetUserId,
        emoji,
        ...(extraCount > 0 ? { extraCount } : {}),
    };
}

export function newLocalReactionId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `local-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Match backend `MIN_INTERVAL_MS` — skip animate/post while still cooling down. */
export const LEAGUE_REACTION_MIN_INTERVAL_MS = 1000;

let lastLocalThrowAt = 0;

/** Returns false when another throw from this tab is still inside the cooldown. */
export function tryConsumeLeagueReactionThrowSlot(): boolean {
    const now = Date.now();
    if (now - lastLocalThrowAt < LEAGUE_REACTION_MIN_INTERVAL_MS) return false;
    lastLocalThrowAt = now;
    return true;
}

const REACTION_BROADCAST = "c8-league-reaction-bc";

/** Broadcast a reaction to any mounted leaderboard (SSE bridge uses the same event). */
export function dispatchLeagueReaction(payload: LeagueReactionPayload): void {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(LEAGUE_REACTION_EVENT, { detail: payload }));
    // Demo / multi-tab: mirror so every open board sees peer throws without SSE.
    try {
        const channel = new BroadcastChannel(REACTION_BROADCAST);
        channel.postMessage(payload);
        channel.close();
    } catch {
        /* BroadcastChannel unavailable */
    }
}

/** Subscribe to cross-tab demo throws. Returns an unsubscribe. */
export function subscribeLeagueReactionBroadcast(
    onReaction: (payload: LeagueReactionPayload) => void,
): () => void {
    if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
        return () => {};
    }
    let channel: BroadcastChannel;
    try {
        channel = new BroadcastChannel(REACTION_BROADCAST);
    } catch {
        return () => {};
    }
    const handler = (ev: MessageEvent) => {
        const parsed = parseLeagueReactionPayload(ev.data);
        if (parsed) onReaction(parsed);
    };
    channel.addEventListener("message", handler);
    return () => {
        channel.removeEventListener("message", handler);
        channel.close();
    };
}

type ReactionMember = {
    userId: number;
    playerName: string;
    isViewer?: boolean;
};

/**
 * `/leaguesdemo` helper: invent an incoming throw from a peer.
 * Prefers targeting the viewer so you can see the receive animation on your row.
 */
export function simulateIncomingLeagueReaction(
    leagueId: string,
    members: ReactionMember[],
    opts?: { targetUserId?: number; emoji?: LeagueReactionEmoji },
): LeagueReactionPayload | null {
    const peers = members.filter((m) => !m.isViewer);
    if (peers.length === 0) return null;
    const from = peers[Math.floor(Math.random() * peers.length)]!;
    const viewer = members.find((m) => m.isViewer);
    const candidates = members.filter((m) => m.userId !== from.userId);
    if (candidates.length === 0) return null;

    let target =
        opts?.targetUserId != null
            ? candidates.find((m) => m.userId === opts.targetUserId) ?? null
            : null;
    // Prefer a mix: sometimes the viewer, otherwise any peer — so the whole board lights up.
    if (!target && viewer && viewer.userId !== from.userId && Math.random() < 0.4) {
        target = viewer;
    }
    if (!target) {
        target = candidates[Math.floor(Math.random() * candidates.length)]!;
    }

    const emoji =
        opts?.emoji ??
        LEAGUE_REACTION_EMOJI_OPTIONS[Math.floor(Math.random() * LEAGUE_REACTION_EMOJI_OPTIONS.length)]!;

    const payload: LeagueReactionPayload = {
        reactionId: newLocalReactionId(),
        leagueId,
        fromUserId: from.userId,
        fromPlayerName: from.playerName,
        targetUserId: target.userId,
        emoji,
    };
    dispatchLeagueReaction(payload);
    // Keep demo standings tallies in sync with simulated throws.
    void import("./leagueStore").then((m) => {
        m.recordPrototypeLeagueReaction({
            leagueId,
            reactionId: payload.reactionId,
            fromUserId: payload.fromUserId,
            targetUserId: payload.targetUserId,
            emoji: payload.emoji,
        });
    });
    return payload;
}
